import { useEffect, useState, useCallback, useRef } from "react";
import debounce from "lodash.debounce";
import { useAuth } from "../../../utils/Auth";
import API from "../../../API";
import styles from "../Settings.module.css";

export default function Personal() {
    const { userObj, currentUser } = useAuth();
    const { updateUserObject } = API;

    // Personal Information state
    const [isEditingName, setIsEditingName] = useState(false);
    const [nameFirst, setNameFirst] = useState("");
    const [nameLast, setNameLast] = useState("");
    const [jobTitle, setJobTitle] = useState("");
    const [role, setRole] = useState("");
    const nameSectionRef = useRef(null);

    const handleUpdateUserObject = useCallback(async (uid, data) => {
        try {
            await updateUserObject(uid, data);
        } catch (error) {
            console.error("Error updating user object:", error.message);
        }
    }, [updateUserObject]);

    // Click outside to close name editing
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (nameSectionRef.current && !nameSectionRef.current.contains(event.target)) {
                setIsEditingName(false);
            }
        };

        if (isEditingName) {
            document.addEventListener("mousedown", handleClickOutside);
        }

        return () => {
            document.removeEventListener("mousedown", handleClickOutside);
        };
    }, [isEditingName]);

    // Initialize state from userObj and orgObj
    useEffect(() => {
        if (userObj && currentUser) {
            setNameFirst(userObj?.name_first || "");
            setNameLast(userObj?.name_last || "");
            setJobTitle(userObj?.job_title || "");

            const isPrimaryUser = userObj.org_id === currentUser.uid;
            const shouldHavePrimaryRole = isPrimaryUser && !userObj.role;
            
            if (shouldHavePrimaryRole) {
                handleUpdateUserObject(currentUser.uid, { role: "primary" });
                setRole("Primary");
            } else {
                const roleValue = userObj?.role || (isPrimaryUser ? "primary" : "No role");
                if (roleValue === "primary") {
                    setRole("Primary");
                } else if (roleValue !== "No role") {
                    setRole(roleValue.charAt(0).toUpperCase() + roleValue.slice(1));
                } else {
                    setRole(roleValue);
                }
            }
        }
    }, [userObj, currentUser, handleUpdateUserObject]);

    const debouncedUpdate = useCallback(
        debounce((uid, data) => {
            handleUpdateUserObject(uid, data);
        }, 1000),
        [handleUpdateUserObject]
    );

    useEffect(() => {
        if (currentUser?.uid && userObj) {
            const hasChanged = 
                nameFirst !== (userObj?.name_first || "") ||
                nameLast !== (userObj?.name_last || "") ||
                jobTitle !== (userObj?.job_title || "");

            if (hasChanged) {
                const data = {
                    name_first: nameFirst,
                    name_last: nameLast,
                    job_title: jobTitle,
                };

                debouncedUpdate(currentUser.uid, data);
            }
        }

        return () => debouncedUpdate.cancel();
    }, [nameFirst, nameLast, jobTitle, currentUser?.uid, userObj, debouncedUpdate]);

    if (!userObj) {
        return null;
    }

    return (
        <div className={styles.section}>
            <h3 className={styles.sectionTitle}>Personal Information</h3>
            <div className={styles.sectionContent}>
                {/* Full Name Field */}
                <div className={styles.fieldGroup} ref={nameSectionRef}>
                    <label className={styles.fieldLabel}>Full Name</label>
                    {!isEditingName ? (
                        <div 
                            className={styles.fieldDisplay} 
                            onClick={() => setIsEditingName(true)}
                        >
                            <span>
                                {nameFirst && nameLast 
                                    ? `${nameFirst} ${nameLast}` 
                                    : "Click to add your name"}
                            </span>
                            <i className="fas fa-edit" />
                        </div>
                    ) : (
                        <div className={styles.nameEditGroup}>
                            <div className={styles.inputWithIcon}>
                                <input
                                    type="text"
                                    className={styles.fieldInput}
                                    placeholder="First Name"
                                    value={nameFirst}
                                    onChange={(e) => setNameFirst(e.target.value)}
                                    maxLength={20}
                                />
                                <i className="fas fa-edit" />
                            </div>
                            <div className={styles.inputWithIcon}>
                                <input
                                    type="text"
                                    className={styles.fieldInput}
                                    placeholder="Last Name"
                                    value={nameLast}
                                    onChange={(e) => setNameLast(e.target.value)}
                                    maxLength={20}
                                />
                                <i className="fas fa-edit" />
                            </div>
                        </div>
                    )}
                </div>

                {/* Job Title Field */}
                <div className={styles.fieldGroup}>
                    <label className={styles.fieldLabel}>Job / Position Title</label>
                    <div className={styles.inputWithIcon}>
                        <input
                            type="text"
                            className={styles.fieldInput}
                            placeholder="Enter your job title"
                            value={jobTitle}
                            onChange={(e) => setJobTitle(e.target.value)}
                            maxLength={50}
                        />
                        <i className="fas fa-edit" />
                    </div>
                </div>

                {/* Organization Role Field */}
                <div className={styles.fieldGroup}>
                    <label className={styles.fieldLabel}>Organization Role</label>
                    <div className={styles.fieldDisplay}>
                        <span>{role || "No role assigned"}</span>
                    </div>
                </div>
            </div>
        </div>
    );
};
